import type { BrandCategory, GetBillingResponse } from '@innovationdepartment/proxima-sdk-axios';
import { Text, useToaster } from '@innovationdepartment/proxima-ui';
import { BRAND_CATEGORY_LABELS } from 'constants/brandCategories';
import { useProximaSDK } from 'hooks';
import { useEffect, useState } from 'react';
import { useBrandStore } from 'stores';

import type { BrandOverviewHookProps } from 'types/brandOverview';
import useBrandOverviewAdsData from './useBrandOverview.AdsData';

function useBrandOverview() {
  const [loading, setLoading] = useState<BrandOverviewHookProps['apiLoading']>({});
  const [billing, setBilling] = useState<GetBillingResponse | null>(null);
  const {
    masterAdAccounts,
    dateRange,
    apiLoading: adsDataLoading,
    audienceSpend,
    audienceSpendSummary,
    audienceStats,
    last30DaysOfAllBrandSpend,
    onConnectAdAccount,
    onDisconnectAdAccount,
    onAudienceDateRangeSelect
  } = useBrandOverviewAdsData();

  /* brand customer settings state */
  const [isFileUploadAllowed, setIsFileUploadAllowed] = useState<boolean>(false);

  const audiencesApi = useProximaSDK('AudiencesApi');
  const brandsApi = useProximaSDK('BrandsApi');
  const billingApi = useProximaSDK('BillingApi');
  const customerUploadsApi = useProximaSDK('CustomerUploadsApi');

  const { showToaster } = useToaster();
  const { brand, updateBrandStore } = useBrandStore();

  const handleLoading = (section: keyof BrandOverviewHookProps['apiLoading'], isLoading = true) => {
    setLoading(prevLoading => ({ ...prevLoading, [section]: isLoading }));
  };

  const onUpdateBrandCategory = async (category: BrandCategory) => {
    handleLoading('brandCategory');
    await brandsApi.updateBrand({ brandId: brand.brandId, updateBrandRequest: { category } });
    updateBrandStore({ ...brand, category });
    // Legacy audience and Meta-integration services still read the category from audiences brand_settings.
    await audiencesApi.updateBrandSettings({
      brandId: brand.brandId,
      updateBrandSettingsRequest: { flavorCategoryId: category }
    });
    const categoryMessage = `Brand category updated to ${BRAND_CATEGORY_LABELS[category]}.`;
    showToaster({ message: categoryMessage, variant: 'success' });
    handleLoading('brandCategory', false);
  };

  const onUpsertBrandCustomerSettings = async (updatedIsFileUploadAllowed: boolean) => {
    await customerUploadsApi.upsertBrandCustomerSettings({
      brandId: brand.brandId,
      upsertBrandCustomerSettings: {
        isFileUploadAllowed: updatedIsFileUploadAllowed
      }
    });

    setIsFileUploadAllowed(updatedIsFileUploadAllowed);

    const enabledOrDisabledText = updatedIsFileUploadAllowed ? 'enabled' : 'disabled';
    const toasterVariant = updatedIsFileUploadAllowed ? 'success' : 'info';

    const brandCustomerSettingsMessage = (
      <Text variant="body2">
        Customer lists {enabledOrDisabledText} for
        <Text variant="body2Semibold"> {brand.name}</Text>
      </Text>
    );

    showToaster({ message: brandCustomerSettingsMessage, variant: toasterVariant });
  };

  const onRefreshBrandBilling = async () => {
    if (!brand?.brandId) return;

    handleLoading('billing');
    try {
      const response = await billingApi.getBilling(
        { brandId: brand.brandId },
        { skipErrorToaster: true }
      );
      setBilling(response.data);
    } catch {
      /* TODO(Jenky): handle error */
    }
    handleLoading('billing', false);
  };

  /* fetch brand billing info */
  // biome-ignore lint/correctness/useExhaustiveDependencies: pre-Biome migration; the app is already working as expected with these dependency arrays
  useEffect(() => {
    onRefreshBrandBilling();
  }, []);

  /* fetch brand customer settings */
  // biome-ignore lint/correctness/useExhaustiveDependencies: pre-Biome migration; the app is already working as expected with these dependency arrays
  useEffect(() => {
    const getFileUploadAllowed = async () => {
      try {
        const { data } = await customerUploadsApi.getBrandCustomerSettings(
          { brandId: brand.brandId },
          { skipErrorToaster: true }
        );

        setIsFileUploadAllowed(data.isFileUploadAllowed);
      } catch {
        /* catch error */
      }
    };

    getFileUploadAllowed();
  }, []);

  return {
    brand,
    billing,
    category: brand.category,
    dateRange,
    audienceSpend,
    audienceSpendSummary,
    audienceStats,
    last30DaysOfAllBrandSpend,
    isFileUploadAllowed,
    onUpdateBrandCategory,
    onAudienceDateRangeSelect,
    onUpsertBrandCustomerSettings,
    onConnectAdAccount,
    onDisconnectAdAccount,
    onRefreshBrandBilling,
    loading: Object.values({ ...loading, ...adsDataLoading }).some(Boolean),
    apiLoading: { ...loading, ...adsDataLoading },
    brandCustomerSettingsLoading: brandsApi.loading,
    masterAdAccounts
  } satisfies Omit<BrandOverviewHookProps, 'brandWarnings'>;
}

export default useBrandOverview;
